/**
 * Sparkline
 * Classification: custom
 *
 * A dependency-free trend line for dense contexts (table cells, stat tiles).
 * Carries no ECharts dependency, so it ships from the root barrel.
 */
import { useId, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { chartTokens } from '@/tokens/chart';
import { severityColorsByMode, statusColors, type SeverityKey } from '@/tokens/colors';
import { buildSparklinePath, type SparklineNormalize } from './sparklineGeometry';

export interface SparklineProps {
  /** Series values, oldest first. */
  data: readonly number[];
  /** `line` draws a stroke only; `area` adds a gradient fill beneath it. */
  variant?: 'line' | 'area';
  /**
   * `minMax` stretches the series across the full height, exaggerating small
   * movements. `zeroMax` anchors the baseline at zero, keeping magnitude honest.
   */
  normalize?: SparklineNormalize;
  /**
   * A categorical series slot index (0-6), a severity key, or `'accent'`.
   *
   * Use `'accent'` when the sparkline shows a single measure rather than one
   * series among several - a volume column in a table, a trend beside a KPI.
   * Series slots encode identity, and borrowing slot 0 for a lone measure
   * implies a categorical relationship that is not there.
   */
  tone?: number | SeverityKey | 'accent';
  showEndPoint?: boolean;
  width?: number;
  height?: number;
  strokeWidth?: number;
  /**
   * Accessible description, e.g. "Alert volume, last 7 days, trending up".
   * Required: a sparkline conveys information and must not be invisible to
   * assistive technology.
   */
  label: string;
}

export function Sparkline({
  data,
  variant = 'line',
  normalize = 'zeroMax',
  tone = 0,
  showEndPoint = false,
  width = 96,
  height = 24,
  strokeWidth = 1.5,
  label,
}: SparklineProps) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'light' ? 'light' : 'dark';
  const gradientId = useId();

  const color =
    typeof tone === 'number'
      ? chartTokens[mode].series[tone % chartTokens[mode].series.length]
      : tone === 'accent'
        ? statusColors.general
        : severityColorsByMode[mode][tone];

  const geometry = useMemo(
    () => buildSparklinePath(data, { width, height, strokeWidth, normalize }),
    [data, width, height, strokeWidth, normalize],
  );

  if (!geometry.line) return null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {variant === 'area' && (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={color}
                stopOpacity={chartTokens[mode].ink.areaOpacity}
              />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={geometry.area} fill={`url(#${gradientId})`} />
        </>
      )}
      <path
        d={geometry.line}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showEndPoint && geometry.endPoint && (
        <circle
          cx={geometry.endPoint.x}
          cy={geometry.endPoint.y}
          r={strokeWidth * 1.6}
          fill={color}
        />
      )}
    </svg>
  );
}
